import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, Building2, MapPin, Layers, QrCode, Camera } from 'lucide-react';
import { toast } from 'sonner';
import EditEntityDialog from '../components/EditEntityDialog';
import RowActions from '../components/RowActions';
import QRScannerDialog from '../components/QRScannerDialog';
import { useNavigate } from 'react-router-dom';

export default function Buildings() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [qrView, setQrView] = useState(null);  // { name, qr_code }

  const [newBuilding, setNewBuilding] = useState({
    name: '',
    address: '',
    floors: 1,
    total_area: 0,
    owner_name: '',
    owner_contact: '',
    year_of_construction: '',
    license_number: '',
    license_validity: '',
    insurance_provider: '',
    insurance_expiry: ''
  });

  useEffect(() => {
    fetchBuildings();
  }, []);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(response.data);
    } catch (error) {
      toast.error('Failed to fetch buildings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBuilding = async () => {
    try {
      const payload = {
        ...newBuilding,
        year_of_construction: newBuilding.year_of_construction ? parseInt(newBuilding.year_of_construction) : null,
        license_validity: newBuilding.license_validity ? new Date(newBuilding.license_validity).toISOString() : null,
        insurance_expiry: newBuilding.insurance_expiry ? new Date(newBuilding.insurance_expiry).toISOString() : null,
      };
      await axios.post(`${API}/buildings`, payload, { withCredentials: true });
      toast.success('Building created successfully');
      setShowNewDialog(false);
      setNewBuilding({ name: '', address: '', floors: 1, total_area: 0, owner_name: '', owner_contact: '', year_of_construction: '', license_number: '', license_validity: '', insurance_provider: '', insurance_expiry: '' });
      fetchBuildings();
    } catch (error) {
      toast.error('Failed to create building');
    }
  };

  return (
    <Layout>
      <div data-testid="buildings-page" className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('bldg.title')}</h1>
            <p className="text-muted-foreground">{t('bldg.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="building-scan-qr-btn" variant="outline" onClick={() => setScanning(true)}>
              <Camera className="w-4 h-4 mr-2 text-emerald-600" />Scan QR
            </Button>
          {['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role) && (
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild>
                <Button data-testid="new-building-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('bldg.add')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Register New Building</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Building Name</Label>
                    <Input
                      data-testid="building-name-input"
                      value={newBuilding.name}
                      onChange={(e) => setNewBuilding({ ...newBuilding, name: e.target.value })}
                      placeholder="e.g., Tower A"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      data-testid="building-address-input"
                      value={newBuilding.address}
                      onChange={(e) => setNewBuilding({ ...newBuilding, address: e.target.value })}
                      placeholder="Full address"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Number of Floors</Label>
                      <Input
                        type="number"
                        value={newBuilding.floors}
                        onChange={(e) => setNewBuilding({ ...newBuilding, floors: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Total Area (sq ft)</Label>
                      <Input
                        type="number"
                        value={newBuilding.total_area}
                        onChange={(e) => setNewBuilding({ ...newBuilding, total_area: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Owner Name</Label>
                      <Input data-testid="building-owner-name-input" value={newBuilding.owner_name} onChange={(e) => setNewBuilding({ ...newBuilding, owner_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Owner Contact</Label>
                      <Input data-testid="building-owner-contact-input" value={newBuilding.owner_contact} onChange={(e) => setNewBuilding({ ...newBuilding, owner_contact: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Year of Construction</Label>
                      <Input type="number" value={newBuilding.year_of_construction} onChange={(e) => setNewBuilding({ ...newBuilding, year_of_construction: e.target.value })} placeholder="2010" />
                    </div>
                    <div>
                      <Label>License Number</Label>
                      <Input value={newBuilding.license_number} onChange={(e) => setNewBuilding({ ...newBuilding, license_number: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>License Validity</Label>
                      <Input type="date" data-testid="building-license-validity-input" value={newBuilding.license_validity} onChange={(e) => setNewBuilding({ ...newBuilding, license_validity: e.target.value })} />
                    </div>
                    <div>
                      <Label>Insurance Provider</Label>
                      <Input value={newBuilding.insurance_provider} onChange={(e) => setNewBuilding({ ...newBuilding, insurance_provider: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Insurance Expiry</Label>
                    <Input type="date" data-testid="building-insurance-expiry-input" value={newBuilding.insurance_expiry} onChange={(e) => setNewBuilding({ ...newBuilding, insurance_expiry: e.target.value })} />
                  </div>
                  <Button
                    data-testid="submit-building-btn"
                    onClick={handleCreateBuilding}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={!newBuilding.name || !newBuilding.address}
                  >
                    Create Building
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {buildings.map((building) => (
              <Card
                key={building.id}
                data-testid={`building-card-${building.id}`}
                className="p-6 hover:shadow-xl transition-all relative group"
              >
                {['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role) && (
                  <div className="absolute top-3 right-3 z-10 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      data-testid={`building-qr-btn-${building.id}`}
                      size="sm"
                      variant="ghost"
                      className="p-1 h-8 w-8"
                      onClick={async () => {
                        try {
                          const r = await axios.get(`${API}/buildings/${building.id}/qr`, { withCredentials: true });
                          setQrView({ name: building.name, qr_code: r.data.qr_code, id: building.id });
                        } catch (e) { toast.error('Failed to load QR'); }
                      }}
                      title="View / Print QR"
                    ><QrCode className="w-4 h-4 text-emerald-600" /></Button>
                    <RowActions
                      testIdPrefix={`building-${building.id}`}
                      onView={() => window.location.href = `/buildings/${building.id}`}
                      onEdit={() => setEditingId(building.id)}
                      onDelete={async () => {
                        try {
                          await axios.delete(`${API}/buildings/${building.id}`, { withCredentials: true });
                          toast.success('Deleted'); fetchBuildings();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
                      }}
                      deleteLabel={`building "${building.name}"`}
                    />
                  </div>
                )}
                <div onClick={() => window.location.href = `/buildings/${building.id}`} className="cursor-pointer">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Building2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    building.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {building.status}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{building.name}</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{building.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    <span>{building.floors} Floors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{building.total_area?.toLocaleString()} sq ft</span>
                  </div>
                </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {buildings.length === 0 && !loading && (
          <Card className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Buildings Found</h3>
            <p className="text-muted-foreground">Add your first building to get started</p>
          </Card>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={buildings.find(b => b.id === editingId)}
            endpoint="buildings"
            title="Edit Building"
            onSaved={() => { setEditingId(null); fetchBuildings(); }}
            fields={[
              { key: 'name', label: 'Building Name' },
              { key: 'address', label: 'Address', type: 'textarea', rows: 2 },
              { key: 'floors', label: 'Floors', type: 'number' },
              { key: 'total_area', label: 'Total Area (sq ft)', type: 'number' },
              { key: 'owner_name', label: 'Owner Name' },
              { key: 'owner_contact', label: 'Owner Contact' },
              { key: 'year_of_construction', label: 'Year of Construction', type: 'number' },
              { key: 'license_number', label: 'License Number' },
              { key: 'license_validity', label: 'License Validity', type: 'date' },
              { key: 'insurance_provider', label: 'Insurance Provider' },
              { key: 'insurance_expiry', label: 'Insurance Expiry', type: 'date' },
              { key: 'location_lat', label: 'Latitude', type: 'number' },
              { key: 'location_lng', label: 'Longitude', type: 'number' },
              { key: 'status', label: 'Status', type: 'select', options: [
                { value: 'active', label: 'Active' },
                { value: 'under_maintenance', label: 'Under Maintenance' },
                { value: 'inactive', label: 'Inactive' },
              ]},
            ]}
          />
        )}

        {/* QR Scanner (camera) */}
        <QRScannerDialog
          open={scanning}
          onOpenChange={setScanning}
          title="Scan Building QR"
          onScan={async (code) => {
            try {
              const r = await axios.get(`${API}/scan/lookup?code=${encodeURIComponent(code)}`, { withCredentials: true });
              if (r.data.type === 'building') {
                toast.success(`Found: ${r.data.entity.name}`);
                navigate(`/buildings/${r.data.entity.id}`);
              } else if (r.data.type === 'asset') {
                toast.success(`Asset found: ${r.data.entity.name}`);
                navigate(`/assets`);
              }
            } catch (e) { toast.error(e.response?.data?.detail || 'Not found'); }
          }}
        />

        {/* QR Code viewer / print */}
        {qrView && (
          <Dialog open={!!qrView} onOpenChange={() => setQrView(null)}>
            <DialogContent className="max-w-sm" data-testid="building-qr-dialog">
              <DialogHeader><DialogTitle>{qrView.name} — QR</DialogTitle></DialogHeader>
              <div className="space-y-3 text-center">
                {qrView.qr_code && (
                  <img src={qrView.qr_code} alt="Building QR" className="mx-auto rounded border border-slate-200 bg-white p-2 max-w-[260px]" />
                )}
                <p className="text-xs text-muted-foreground">Print this sticker and place it at the building entrance. Field technicians can scan it to dispatch a Work Order.</p>
                <div className="flex gap-2 justify-center">
                  <Button data-testid="building-qr-print" size="sm" variant="outline" onClick={() => {
                    const w = window.open('', '_blank', 'width=400,height=500');
                    if (!w) return;
                    w.document.write(`<html><head><title>QR — ${qrView.name}</title></head><body style="text-align:center;font-family:sans-serif"><h2>${qrView.name}</h2><img src="${qrView.qr_code}" style="width:300px;height:300px" /><p style="font-size:11px;color:#666">FM Flexi Pro™ — Scan to dispatch</p><script>window.onload=()=>{window.print();}</script></body></html>`);
                    w.document.close();
                  }}>Print</Button>
                  <Button data-testid="building-qr-close" size="sm" onClick={() => setQrView(null)}>Close</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
